import React, { useState } from 'react';
import { LENDER_NAME_MAX_LENGTH } from '../../../constants/clients.constants';

interface CreateLenderModalProps {
  isOpen: boolean;
  clientName: string;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
}

export const CreateLenderModal: React.FC<CreateLenderModalProps> = ({
  isOpen,
  clientName,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmed = name.trim();

    if (!trimmed) {
      setError('Lender name is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await onSubmit(trimmed);
      setName('');
      setError('');
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create lender';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (): void => {
    setName('');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md mx-4">
        <div className="border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Add Lender</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Add a new lender to <span className="font-medium text-slate-700">{clientName}</span>.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5">
            <label htmlFor="lender-name" className="block text-sm font-medium text-slate-700 mb-1.5">
              Lender Name
            </label>
            <input
              id="lender-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={LENDER_NAME_MAX_LENGTH}
              placeholder="e.g. Goodleap"
              autoFocus
              disabled={isSubmitting}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors disabled:opacity-50"
            />
            {error && (
              <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Adding...' : 'Add Lender'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
